import {
  ArrowsClockwiseIcon,
  ArrowUpIcon,
  CalendarIcon,
  CheckCircleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, formatDate } from '@/utils';

interface ForecastItem {
  id: string;
  name: string;
  sku: string;
  currentStock: number;
  forecastedDemand: number;
  suggestedOrder: number;
  confidence: number;
  riskLevel: 'low' | 'medium' | 'high';
  stockoutDate: string | null;
  recommendations: string[];
  historicalData: {
    date: string;
    demand: number;
  }[];
}

interface DemandForecastData {
  items: ForecastItem[];
  summary: {
    totalItems: number;
    highRiskItems: number;
    averageConfidence: number;
    projectedStockouts: number;
    suggestedOrderValue: number;
  };
  methodology: string;
  accuracy: number;
  lastUpdated: string;
}

interface DemandForecastProps {
  period?: string;
  horizon?: string;
  className?: string;
}

const DemandForecast: React.FC<DemandForecastProps> = ({
  period = '90d',
  horizon = '30d',
  className = '',
}) => {
  const [data, setData] = useState<DemandForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeRiskFilter, setActiveRiskFilter] = useState<
    'all' | 'low' | 'medium' | 'high'
  >('all');
  const [selectedHorizon, setSelectedHorizon] = useState(horizon);

  const api = useEnhancedApi();
  const { toast } = useToast();
  const showToast = (message: string, success: boolean = true) => {
    toast({
      title: success ? 'Success' : 'Error',
      description: message,
      variant: success ? 'default' : 'destructive',
    });
  };

  useEffect(() => {
    fetchDemandForecast();
  }, [period, selectedHorizon]);

  const fetchDemandForecast = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.inventory.getDemandForecast({
        period,
        horizon: selectedHorizon,
      });
      setData(response.data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch demand forecast';
      setError(errorMessage);
      showToast(errorMessage, false);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low':
        return 'text-success-60 bg-success-5 border-success-20';
      case 'medium':
        return 'text-warning-60 bg-warning-5 border-warning-20';
      case 'high':
        return 'text-destructive-60 bg-destructive-5 border-destructive-20';
    }
  };

  const getRiskIcon = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low':
        return <CheckCircleIcon className='h-4 w-4 text-success-60' />;
      case 'medium':
        return <WarningCircleIcon className='h-4 w-4 text-warning-60' />;
      case 'high':
        return <WarningCircleIcon className='h-4 w-4 text-destructive-60' />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-success-60';
    if (confidence >= 60) return 'text-warning-60';
    return 'text-destructive-60';
  };

  const getFilteredItems = () => {
    if (!data || activeRiskFilter === 'all') return data?.items || [];
    return data.items.filter(item => item.riskLevel === activeRiskFilter);
  };

  const handleRefresh = () => {
    fetchDemandForecast();
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <ArrowUpIcon className='h-5 w-5' />
            <Skeleton className='h-6 w-40' />
          </div>
          <Skeleton className='h-4 w-80' />
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex gap-4'>
            <Skeleton className='h-10 w-32' />
            <Skeleton className='h-10 w-32' />
          </div>
          <div className='grid grid-cols-5 gap-4'>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className='h-20' />
            ))}
          </div>
          <div className='space-y-2'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className='h-24 w-full' />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className='pt-6'>
          <Alert variant='destructive'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <ArrowUpIcon className='h-5 w-5 text-brand-60' />
            <div>
              <CardTitle>Demand Forecasting</CardTitle>
              <CardDescription>
                AI-powered demand prediction for optimal inventory planning
              </CardDescription>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Badge variant='outline' className='text-xs'>
              {data.accuracy}% accuracy
            </Badge>
            <Button
              hierarchy='outlined'
              size='sm'
              onClick={handleRefresh}
              disabled={loading}
            >
              <ArrowsClockwiseIcon
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Controls */}
        <div className='flex items-center gap-4 mb-6'>
          <div className='flex items-center gap-2'>
            <CalendarIcon className='h-4 w-4 text-gray-60' />
            <Select value={selectedHorizon} onValueChange={setSelectedHorizon}>
              <SelectTrigger className='w-32'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='7d'>7 days</SelectItem>
                <SelectItem value='14d'>14 days</SelectItem>
                <SelectItem value='30d'>30 days</SelectItem>
                <SelectItem value='60d'>60 days</SelectItem>
                <SelectItem value='90d'>90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Badge variant='outline' className='text-xs'>
            Method: {data.methodology}
          </Badge>

          <Badge variant='outline' className='text-xs'>
            Updated: {data.lastUpdated}
          </Badge>
        </div>

        {/* Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-5 gap-4 mb-6'>
          <Card>
            <CardContent className='p-4 text-center'>
              <div className='text-2xl font-bold'>
                {data.summary.totalItems}
              </div>
              <div className='text-sm text-gray-60'>Total Items</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4 text-center'>
              <div className='text-2xl font-bold text-destructive-60'>
                {data.summary.highRiskItems}
              </div>
              <div className='text-sm text-gray-60'>High Risk</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4 text-center'>
              <div
                className={`text-2xl font-bold ${getConfidenceColor(data.summary.averageConfidence)}`}
              >
                {data.summary.averageConfidence}%
              </div>
              <div className='text-sm text-gray-60'>Avg Confidence</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4 text-center'>
              <div className='text-2xl font-bold text-warning-60'>
                {data.summary.projectedStockouts}
              </div>
              <div className='text-sm text-gray-60'>Stockouts</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-4 text-center'>
              <div className='text-2xl font-bold text-brand-60'>
                {formatCurrency(data.summary.suggestedOrderValue)}
              </div>
              <div className='text-sm text-gray-60'>Order Value</div>
            </CardContent>
          </Card>
        </div>

        {/* Risk Filter Tabs */}
        <Tabs
          value={activeRiskFilter}
          onValueChange={value =>
            setActiveRiskFilter(value as 'all' | 'low' | 'medium' | 'high')
          }
        >
          <TabsList className='grid w-full grid-cols-4'>
            <TabsTrigger value='all'>All Items</TabsTrigger>
            <TabsTrigger value='high'>High Risk</TabsTrigger>
            <TabsTrigger value='medium'>Medium Risk</TabsTrigger>
            <TabsTrigger value='low'>Low Risk</TabsTrigger>
          </TabsList>

          {/* Items List */}
          <TabsContent value={activeRiskFilter} className='mt-4'>
            <div className='space-y-3 max-h-96 overflow-y-auto'>
              {getFilteredItems().map(item => (
                <Card key={item.id} className='p-4'>
                  <div className='space-y-4'>
                    {/* Header */}
                    <div className='flex items-start justify-between'>
                      <div className='flex items-center gap-4'>
                        <div className='flex items-center gap-2'>
                          {getRiskIcon(item.riskLevel)}
                          <Badge className={getRiskColor(item.riskLevel)}>
                            {item.riskLevel.toUpperCase()} RISK
                          </Badge>
                        </div>
                        <div>
                          <div className='font-medium'>{item.name}</div>
                          <div className='text-sm text-gray-60'>{item.sku}</div>
                        </div>
                      </div>

                      <div className='text-right'>
                        <div
                          className={`font-medium ${getConfidenceColor(item.confidence)}`}
                        >
                          {item.confidence}% confidence
                        </div>
                        {item.stockoutDate && (
                          <div className='text-sm text-destructive-60'>
                            Stockout: {formatDate(item.stockoutDate)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className='grid grid-cols-3 gap-4 bg-gray-5 rounded-lg p-3'>
                      <div className='text-center'>
                        <div className='text-lg font-semibold'>
                          {item.currentStock}
                        </div>
                        <div className='text-xs text-gray-60'>
                          Current Stock
                        </div>
                      </div>
                      <div className='text-center'>
                        <div className='text-lg font-semibold text-brand-60'>
                          {item.forecastedDemand}
                        </div>
                        <div className='text-xs text-gray-60'>
                          Forecasted Demand
                        </div>
                      </div>
                      <div className='text-center'>
                        <div className='text-lg font-semibold text-success-60'>
                          {item.suggestedOrder}
                        </div>
                        <div className='text-xs text-gray-60'>
                          Suggested Order
                        </div>
                      </div>
                    </div>

                    {/* Recommendations */}
                    {item.recommendations.length > 0 && (
                      <div className='bg-blue-5 rounded-lg p-3'>
                        <div className='text-sm font-medium text-blue-70 mb-2'>
                          Recommendations:
                        </div>
                        <ul className='space-y-1'>
                          {item.recommendations.map((rec, index) => (
                            <li
                              key={index}
                              className='text-xs text-blue-60 flex items-start gap-1'
                            >
                              <div className='h-1 w-1 rounded-full bg-blue-60 mt-2 flex-shrink-0' />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </Card>
              ))}

              {getFilteredItems().length === 0 && (
                <div className='text-center py-8 text-gray-60'>
                  No items found for the selected risk level
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default DemandForecast;
